import React from 'react';
import { Col, Row, FormGroup } from 'react-bootstrap';
import { TextBox, MaskedBox, DatePicker, TriStateSwitch, Switch } from 'fw/forms';
import { Label, ActionButton } from 'fw/components';
import { SetupTab } from 'areas/shared/pages/setup';
import * as App from 'modules/app/App'
import * as API from 'modules/app/API'
import SystemAPI from 'fw/system/API'
import { ServiceBranchCombo, EthnicityCombo, GenderCombo, DocumentUpload } from 'areas/shared/controls';
import { FileGrid } from 'areas/shared/components';
import * as Modals from 'fw/globals/Modals';
import * as Formatting from 'fw/misc/Formatting';
import * as FormManager from 'fw/forms/form/FormManager';
import DD214StatusLabel from 'areas/admin/shared/DD214StatusLabel';

class DD214InfoTab extends SetupTab {
    initialForm() {
        let dD214File = {
            storageToken: null,
            fileName: ""
        }
        return {
            veteranOwnerDD214Id: 0,
            otherFiles: [],
            dD214File,
            sdvet: false,
            confirmReviewReset: false
        }
    }

    urls() {
        return {
            get: API.Admin.url('/dd214/getDD214Info'),
            save: API.Admin.url('/dd214/saveDD214Info'),
        }
    }

    getRequest(selectedId) {
        return {
            dd214Key: selectedId
        }
    }

    saveRequest() {
        return {
            veteranOwnerDD214Id: this.state.form.veteranOwnerDD214Id,
            ownerSSN: this.state.form.ownerSSN,
            firstName: this.state.form.firstName,
            lastName: this.state.form.lastName,
            serviceBranchId: this.state.form.serviceBranchId,
            ethnicityId: this.state.form.ethnicityId,
            birthday: this.state.form.birthday,
            genderCode: this.state.form.genderCode,
            otherFiles: this.state.form.otherFiles,
            dD214File: this.state.form.dD214File,
            caReviewValidDocument: this.state.form.caReviewValidDocument,
            mgmtReviewValidDocument: this.state.form.mgmtReviewValidDocument,
            sdvet: this.state.form.sdvet,
            confirmReviewReset: this.state.form.confirmReviewReset || false
        }
    }

    privileges() {
        return {
            edit: App.hasPrivilege("AdminDD214Setup"),
            add: App.hasPrivilege("AdminDD214Setup"),
        }
    }

    ready = () => {
        this.focusControl && this.focusControl.focus();
    }

    downloadFile = (storageToken, fileName) => {
        new SystemAPI().download({
            storageToken: storageToken,
            fileName: fileName
        });
    };

    handleDocumentListChange = () => {
        this.setState({ dirty: true });
        FormManager.dirt(this._form);
    }

    handleDelete = () => {
        const me = this;
        Modals.remove({
            title: "Deleting DD214",
            elementType: "DD214 for SSN",
            name: this.state.form.ownerSSN,
            onYes: () => {
                API.Admin.post('/dd214/deleteDD214', 
                    { 
                        veteranOwnerDD214Id: this.state.form.veteranOwnerDD214Id,
                        ownerSSN: this.props.selectedId 
                    },
                    {
                        success: (r) => {
                            me.props.onSuccess(r.message);
                            me.cancel();
                        },
                        error: (r) => {
                            me.props.onError(r.error);
                        }
                    });
            }
        });
    }

    afterSave = (entityId, data) => {
        let form = this.state.form;
        
        const otherfiles = data.otherFiles;
        form.otherfiles = otherfiles;

        form.veteranOwnerDD214Id = data.veteranOwnerDD214Id;
        form.caReviewUserName = data.caReviewUserName;
        form.caReviewDate = data.caReviewDate;
        form.mgmtReviewUserName = data.mgmtReviewUserName;
        form.mgmtReviewDate = data.mgmtReviewDate;
        form.sdvet = data.sdvet;
        form.confirmReviewReset = false;

        this.setState({ form, showDelete: true });
    }

    renderDelete() {
        if (!this.state.showDelete && this.state.form.veteranOwnerDD214Id === 0)
            return null;

        return (
            <div className="pull-right">
                <ActionButton id="btnDeleteUser" onClick={this.handleDelete} icon="trash">Delete DD214 Record</ActionButton>
            </div>
        )
    }

    handledd214UploadChange = (file) => {
        let form = this.state.form;

        const fileItem = {
            entityId: null,
            fileName: file.fileName,
            storageToken: file.storageToken
        };
        
        form.dD214File = fileItem;
        form.caReviewValidDocument = null;
        form.caReviewUserName = null;
        form.caReviewDate = null;
        form.mgmtReviewValidDocument = null;
        form.mgmtReviewUserName = null;
        form.mgmtReviewDate = null;
        this.setState({ form });
        this.handleDocumentListChange();
    }

    handleCaReviewChange = (value) => {
        let form = this.state.form;
        form.caReviewUserName = null;
        form.caReviewValidDocument = value;
        this.setState({ form });
    }

    handleMgmtReviewChange = (value) => {
        let form = this.state.form;
        form.mgmtReviewUserName = null;
        form.mgmtReviewValidDocument = value;
        this.setState({ form });
    }

    // NVBDC-1403: a Yes -> No transition resets both CA and Management Review (backend requires
    // confirmReviewReset for exactly this reason) -- confirm before applying, and null the same six
    // review fields handledd214UploadChange already nulls on a file replacement, so the existing
    // "did the review change" save logic performs the reset without any new backend nulling code.
    handleSdvetChange = (value) => {
        const me = this;

        if (this.state.form.sdvet && !value) {
            Modals.confirm(
                "Changing SDVET to 'No' will reset both CA Review and Management Review, requiring both to be redone. <br /><br />" +
                "If you wish to continue, please select 'Yes'. <br /><br />" +
                "If you wish to keep SDVET as 'Yes', please select 'No', and you will be returned to the previous screen.",
                function () {
                    let form = me.state.form;
                    form.sdvet = false;
                    form.confirmReviewReset = true;
                    form.caReviewValidDocument = null;
                    form.caReviewUserName = null;
                    form.caReviewDate = null;
                    form.mgmtReviewValidDocument = null;
                    form.mgmtReviewUserName = null;
                    form.mgmtReviewDate = null;
                    me.setState({ form });
                    me.handleDocumentListChange();
                }
            );
            return;
        }

        let form = this.state.form;
        form.sdvet = value;
        this.setState({ form });
        this.handleDocumentListChange();
    }

    renderForm() {
        let form = this.state.form;
        
        let fileUploaded = form.dD214File && form.dD214File.storageToken !== null && form.dD214File.storageToken !== '';
        let dependentValues = { 
            caReviewValidDocument : form.caReviewValidDocument,
            mgmtReviewValidDocument : form.mgmtReviewValidDocument
        };
        
        let caReviewLabelColor = form.caReviewValidDocument ? 'green' : 'red';
        let mgmtReviewLabelColor = form.mgmtReviewValidDocument ? 'green' : 'red';
        return (
            <React.Fragment>
                <Row>
                    <Col md={12}>
                        <h2 className="inline">
                            {this.state.form.lastName} {this.state.form.firstName} &nbsp;
                            <small style={{ position: 'relative', top: '-5px' }}>
                                <DD214StatusLabel
                                    value={fileUploaded}
                                    dependentValues={dependentValues}
                                    emptyLabel=""
                                    labelFormat={true} />
                            </small>
                        </h2>
                    </Col>
                </Row>
                <hr style={{marginTop:"10px"}}/>
                <Row>
                    <Col md={10}>
                        <i>Please be aware that any missing information from the below mandatory fields need to be completed within the Application. This needs to be done before uploading the DD214 file.</i>
                    </Col>
                    <Col md={2}>
                        {this.renderDelete()}
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='firstName' required>
                                First Name
                            </Label>
                            <TextBox
                                disabled={this.state.form.dD214Key !== undefined}
                                required
                                name='firstName'
                                placeholder="First Name"
                                maxLength={40} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='lastName' required>
                                Last Name
                            </Label>
                            <TextBox
                                disabled={this.state.form.dD214Key !== undefined}
                                required
                                name='lastName'
                                placeholder="Last Name"
                                maxLength={40} />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label htmlFor='genderCode'>Gender</Label>
                            <GenderCombo
                                name='genderCode'
                                placeholder="Gender"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label htmlFor='birthDate'>Date of Birth</Label>
                            <DatePicker
                                name='birthday'
                                placeholder='Select Date'
                                showTime={false} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='ethnicityId'>Ethnicity</Label>
                            <EthnicityCombo
                                name='ethnicityId'
                                placeholder="Ethnicity"
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='ownerSSN' required>
                                Owner SSN </Label>
                            <MaskedBox
                                disabled={this.state.form.dD214Key !== undefined}
                                required
                                name='ownerSSN'
                                placeholder='Owner SSN'
                                mask='999-99-9999'
                                alwaysShowMask={false}
                                maskChar=''
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='serviceBranchId' required>
                                Branch of Service
                            </Label>
                            <ServiceBranchCombo
                                disabled={this.state.form.dD214Key !== undefined}
                                required
                                name='serviceBranchId'
                                placeholder="Branch of Service"
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='dD214FileId' required>
                                DD214
                            </Label>
                            <DocumentUpload
                                storageToken={this.state.form.dD214File.storageToken}
                                fileName={this.state.form.dD214File.fileName}
                                onChange={this.handledd214UploadChange}
                                skipConfirmation={false}
                                confirmationMsg="You are about to remove this DD214 file.<br /><br />If you wish to keep this document, please select 'No' and you will be returned to the previous screen.<br /><br />If you wish to Discard this document, select 'Yes'."
                                wrapFileNameLength={1024}
                                useEncryption={true}
                                folder={"dd214"}
                            />
                        </FormGroup>

                        {/* SDVET always renders here, at the same vertical position as "DD214
                            Review" (level with it, not stacked below CA/Management Review) --
                            disabled until a file exists, unlike the review controls below which
                            don't render at all without one. */}
                        <Row>
                            <Col md={6}>
                                {this.state.form.dD214File.storageToken ?
                                    <Label htmlFor='dD214FileReview'>
                                        DD214 Review
                                    </Label>
                                : null}
                            </Col>
                            <Col md={6} style={{ position: 'relative' }}>
                                {/* Absolutely positioned so its own (label-on-top-of-switch)
                                    height never grows this row -- CA Review below must stay put
                                    regardless of whether this column's natural content is taller
                                    than "DD214 Review" alone. */}
                                <div style={{ position: 'absolute', top: 0, right: '30px', zIndex: 10, width: '90px' }}>
                                    <Label htmlFor='sdvet' style={{ marginBottom: '4px' }}>SDVET</Label>
                                    <Switch
                                        disabled={!fileUploaded}
                                        name="sdvet"
                                        labelLeft="No"
                                        labelRight="Yes"
                                        value={this.state.form.sdvet}
                                        onChange={this.handleSdvetChange}
                                        bsStyle="success" />
                                </div>
                            </Col>
                        </Row>

                        {this.state.form.dD214File.storageToken ?
                            <FormGroup>
                                <Row>
                                    <Col md={4}>
                                        <br />
                                        <Label>CA Review</Label>
                                    </Col>
                                    <Col md={8}>
                                        <TriStateSwitch
                                            disabled={!App.hasPrivilege("AdminDD214CAReviewer")}
                                            style={{ paddingTop: '5px' }}
                                            name="caReviewValidDocument"
                                            labelLeft="Decline"
                                            labelRight="Approve"
                                            leftColor="red"
                                            onChange={this.handleCaReviewChange}
                                            bsStyle="success" />
                                    </Col>
                                </Row>
                                {form.caReviewUserName && form.caReviewDate ?
                                    <Row>
                                        <Col md={12} style={{textAlign:"left"}}>
                                            <em style={{ color:caReviewLabelColor }}>Review completed on {Formatting.formatClientDate(form.caReviewDate)} by {form.caReviewUserName}</em>
                                        </Col>
                                    </Row>
                                : null}
                                <br />
                                <Row>
                                    <Col md={4}>
                                        <br />
                                        <Label>Management Review</Label>
                                    </Col>
                                    <Col md={8}>
                                        <TriStateSwitch
                                            disabled={!App.hasPrivilege("AdminDD214MgmtReviewer")}
                                            style={{ paddingTop: '5px' }}
                                            name="mgmtReviewValidDocument"
                                            labelLeft="Decline" 
                                            labelRight="Approve"
                                            leftColor="red"
                                            onChange={this.handleMgmtReviewChange}
                                            bsStyle="success" />
                                    </Col>
                                </Row>
                                {form.mgmtReviewUserName && form.mgmtReviewDate ?
                                    <Row>
                                        <Col md={12} style={{textAlign:"left"}}>
                                            <em style={{color:mgmtReviewLabelColor}}>Review completed on {Formatting.formatClientDate(form.mgmtReviewDate)} by {form.mgmtReviewUserName}</em>
                                        </Col>
                                    </Row>
                                : null}
                            </FormGroup>
                        :null}
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor='otherFiles'>
                                Other Supporting Documents
                            </Label>
                            <FileGrid
                                documents={this.state.form.otherFiles}
                                onAdd={this.handleDocumentListChange}
                                onDelete={this.handleDocumentListChange}
                                readonly={this.props.readonly}
                                addedBy={this.state.form.userName}
                                useEncryption={true}
                                wrapFileNameLength={30}
                                folder={"dd214"} />
                        </FormGroup>
                    </Col>
                </Row>
            </React.Fragment >
        )
    }
}

export default DD214InfoTab;



// WEBPACK FOOTER //
// ./src/areas/admin/dd214Setup/dd214Info/DD214InfoTab.js